/**
 * PaperBook — paperlab 物理纸张阅读器
 *
 * 将 storycomet 式的"实体书"体验搬进 3D 场景：
 * - 使用 paperlab <PaperMesh>（无自带 Canvas，可嵌入项目现有 Canvas）
 * - 纸张 surface 质感 + 水彩渲染 + 文本排版（Matt Pocock 严格类型）
 * - 翻页进度由 usePaperFlip 的 gsap 补间驱动，via PaperHandle.set('progress')
 * - 纸张自带 paperlab 光照（PaperLighting），与场景灯光共处
 */
import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { PaperMesh, PaperLighting, type PaperHandle } from 'paperlab'
import { useBookStore } from '../../stores'
import { toPaperPageProps, FLIP_DURATION_MS } from '../../lib/paperlab/paperConfig'

interface PaperBookProps {
  /** 当前页索引（-1 表示无页） */
  index: number
  /** 页面视觉数据（imageUrl 可选，text 必填） */
  imageUrl?: string
  text?: string
  title?: string
}

/**
 * 单选当前页的纸张阅读器。
 *
 * 布局：纸张以书脊为轴位于场景中央（z=0.2），略高于地板；
 * 翻页时 paperlab 的 flip behavior 驱动纸张绕书脊真实弯曲。
 * 纸张正对相机（rotation 绕 Y 轴 0），页面向右翻开（spine: 'right'）。
 */
export default function PaperBook({ index, imageUrl, text = '', title }: PaperBookProps) {
  const handleRef = useRef<PaperHandle>(null)
  const { flipProgress, isFlipping, direction } = useBookStore()
  const lastProgressRef = useRef(0)

  // 页面配置：优先插画，回退文字排版
  const props = toPaperPageProps(
    {
      imageUrl: imageUrl && imageUrl.length > 2 ? imageUrl : undefined,
      text,
      title,
    },
    Math.max(0, index),
  )

  // 翻页方向决定 spine：下一页 → 书脊在右（right）；上一页 → 书脊在左（left）
  const spine: 'left' | 'right' = direction === 'prev' ? 'left' : 'right'

  // 每次换页重置进度
  useEffect(() => {
    lastProgressRef.current = 0
  }, [index])

  // 每帧把 store 的 flipProgress 写入 paperlab handle（避免 React 重渲染）
  useFrame(() => {
    const handle = handleRef.current
    if (!handle) return
    const target = isFlipping ? flipProgress : 0
    lastProgressRef.current = target
    handle.set('progress', target)
  })

  return (
    <group position={[0, 0, 0.2]}>
      {/* paperlab 专用光照：保证纸张 PBR 渲染正确（与场景灯光叠加） */}
      <PaperLighting
        preset="studio"
        light={{ exposure: 1.15, ambient: 0.55 }}
        floor={-1.5}
        scale={12}
        contactShadow={false}
        environment={false}
      />

      <PaperMesh
        ref={handleRef}
        key={props.key}
        sheet={props.sheet}
        stock={props.stock}
        content={props.content}
        surface={props.surface}
        behavior={{
          type: 'flip',
          progress: 0,
          spine,
          radius: 0.6,
        }}
        position={props.position}
        rotation={props.rotation}
        interactive={false}
        autoplay={false}
      />
    </group>
  )
}

/**
 * 纸面文字内容换算：storycomet 式绘本纸面比例 3:4，
 * flip 时预留翻页动画时长窗口。
 */
export const PAPER_FLIP_WINDOW_MS = FLIP_DURATION_MS

// ─── 辅助：纸张边角阴影（可选增强） ───
export function PaperContactShadow({ width, height }: { width: number; height: number }) {
  const meshRef = useRef<THREE.Mesh>(null)
  useFrame(() => {
    if (meshRef.current) {
      // 静态投影占位（真实投影由 PaperLighting contactShadow 提供时禁用）
    }
  })
  return (
    <mesh ref={meshRef} position={[0, -1.48, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[width * 2.2, height * 2.2]} />
      <meshBasicMaterial color="#000000" transparent opacity={0.18} />
    </mesh>
  )
}